import { useMutation, useQuery, type UseQueryResult } from '@tanstack/react-query'
import { useCallback, useState } from 'react'

import {
  getResearchJob,
  isSettled,
  type KbSyncFile,
  RESEARCH_POLL_INTERVAL_MS,
  type ResearchJob,
  startKbSync,
  startResearch,
} from '@/lib/api/endpoints/ai-research'

const ACTIVE_JOB_PREFIX = 'canopy.research.job.'

function jobKey(companyId: string): string {
  return `${ACTIVE_JOB_PREFIX}${companyId}`
}

export function storedJobId(companyId: string | undefined): string | null {
  if (companyId === undefined) return null

  try {
    return sessionStorage.getItem(jobKey(companyId))
  } catch {
    return null
  }
}

function rememberJob(companyId: string, jobId: string): void {
  try {
    sessionStorage.setItem(jobKey(companyId), jobId)
  } catch {}
}

function forgetJob(companyId: string): void {
  try {
    sessionStorage.removeItem(jobKey(companyId))
  } catch {}
}

export function useResearchJob(jobId: string | null): UseQueryResult<ResearchJob> {
  return useQuery({
    queryKey: ['research-job', jobId],
    queryFn: () => getResearchJob(jobId ?? ''),
    enabled: jobId !== null,
    staleTime: 0,
    retry: false,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return status !== undefined && isSettled(status) ? false : RESEARCH_POLL_INTERVAL_MS
    },
  })
}

export interface ResearchTrigger {
  jobId: string | null
  job: UseQueryResult<ResearchJob>

  isRunning: boolean

  refresh: (companyId: string) => void
  sync: (input: { companyId: string; companyName: string; files: readonly KbSyncFile[] }) => void

  error: string | null

  dismiss: () => void
}

export function useResearchTrigger(companyId: string | undefined): ResearchTrigger {
  const [jobId, setJobId] = useState<string | null>(() => storedJobId(companyId))
  const [followed, setFollowed] = useState<string | undefined>(companyId)

  if (followed !== companyId) {
    setFollowed(companyId)
    setJobId(storedJobId(companyId))
  }

  const job = useResearchJob(jobId)

  const accept = useCallback((forCompany: string, id: string) => {
    rememberJob(forCompany, id)
    setJobId(id)
  }, [])

  const refreshMutation = useMutation({
    mutationFn: (forCompany: string) => startResearch(forCompany),
    onSuccess: (started, forCompany) => {
      accept(forCompany, started.job_id)
    },
  })

  const syncMutation = useMutation({
    mutationFn: (input: { companyId: string; companyName: string; files: readonly KbSyncFile[] }) =>
      startKbSync(input),
  })

  const status = job.data?.status
  const isRunning =
    refreshMutation.isPending ||
    syncMutation.isPending ||
    (jobId !== null && status !== undefined && !isSettled(status))

  const failure = refreshMutation.error ?? syncMutation.error

  return {
    jobId,
    job,
    isRunning,

    refresh: (forCompany: string) => {
      refreshMutation.mutate(forCompany)
    },
    sync: (input) => {
      syncMutation.mutate(input)
    },

    error: failure === null ? null : failure.message,

    dismiss: () => {
      if (companyId !== undefined) forgetJob(companyId)
      setJobId(null)
    },
  }
}
